/**
 * Pritect Beacon — Cookie Declaration Widget
 *
 * Embeddable widget that renders a live, auto-updating cookie declaration table.
 * Reads data from the public cookie-declaration Edge Function.
 *
 * Usage:
 * <script src=".../pritect-declaration.min.js" data-domain-id="..." data-lang="fr" async></script>
 * <div id="pritect-cookie-declaration"></div>
 */

import { defaultTranslations, resolveTranslation, RTL_LANGUAGES, type TranslationStrings } from "../i18n/translations";

interface CookieEntry {
  name: string;
  provider: string | null;
  category: string;
  purpose: string | null;
  duration: string | null;
  duration_seconds: number | null;
  is_third_party: boolean;
  lawful_basis: string | null;
}

interface DeclarationResponse {
  domain: string;
  domain_id: string;
  last_scanned: string | null;
  total_cookies: number;
  categories: Record<string, CookieEntry[]>;
  generated_at: string;
}

function t(key: keyof TranslationStrings, lang: string): string {
  return resolveTranslation(key, lang, null);
}

/**
 * Localized label + description for an API category key. The API's "unknown"
 * bucket maps to the "unclassified" translation keys; a category key we have
 * no translation for falls back to the raw key as its label.
 */
export function categoryCopy(cat: string, lang: string): { label: string; description: string } {
  const key = cat === "unknown" ? "unclassified" : cat;
  const labelKey = `cat_${key}`;
  if (!(labelKey in defaultTranslations["en"])) {
    return { label: cat, description: "" };
  }
  return {
    label: t(labelKey as keyof TranslationStrings, lang),
    description: t(`catDesc_${key}` as keyof TranslationStrings, lang),
  };
}

/** Localized unit string, e.g. "30 days" / "30 jours". Falls back to English. */
function formatUnit(value: number, unit: "day" | "month" | "year", lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: "unit", unit, unitDisplay: "long" }).format(value);
  } catch {
    return `${value} ${unit}${value !== 1 ? "s" : ""}`;
  }
}

export function formatDuration(duration: string | null, seconds: number | null, lang: string): string {
  if (duration) return duration;
  if (!seconds || seconds <= 0) return t("declSession", lang);
  const days = Math.round(seconds / 86400);
  if (days < 1) return t("declSession", lang);
  if (days < 30) return formatUnit(days, "day", lang);
  const months = Math.round(days / 30);
  if (months < 12) return formatUnit(months, "month", lang);
  return formatUnit(Math.round(days / 365), "year", lang);
}

function getStyles(theme: "light" | "dark"): string {
  const bg = theme === "dark" ? "#0a0a14" : "#ffffff";
  const bgCard = theme === "dark" ? "#111122" : "#f8f9fa";
  const bgHeader = theme === "dark" ? "#16162a" : "#eef0f4";
  const text = theme === "dark" ? "#e0e0e8" : "#1a1a2e";
  // Muted text must stay ≥4.5:1 on the light/dark surfaces it sits on
  // (the previous 40%/50% alphas landed near 2.5-3.9:1, failing WCAG AA).
  const textMuted = theme === "dark" ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.62)";
  const border = theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)";
  const accent = "#7c3aed";
  const badge = theme === "dark" ? "rgba(124,58,237,0.15)" : "rgba(124,58,237,0.1)";

  return `
    .pb-decl { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: ${text}; background: ${bg}; border-radius: 12px; overflow: hidden; border: 1px solid ${border}; }
    .pb-decl-header { padding: 20px 24px; border-bottom: 1px solid ${border}; }
    .pb-decl-title { font-size: 16px; font-weight: 600; margin: 0 0 4px; }
    .pb-decl-subtitle { font-size: 12px; color: ${textMuted}; margin: 0; }
    .pb-decl-cat { border-bottom: 1px solid ${border}; }
    .pb-decl-cat:last-child { border-bottom: none; }
    .pb-decl-cat-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 24px; cursor: pointer; background: ${bgCard}; user-select: none; transition: background 0.15s; width: 100%; text-align: start; border: none; font: inherit; color: inherit; }
    .pb-decl-cat-header:hover { background: ${bgHeader}; }
    .pb-decl-cat-header:focus-visible { outline: 2px solid ${accent}; outline-offset: -2px; }
    .pb-decl-cat-label { font-size: 14px; font-weight: 600; }
    .pb-decl-cat-desc { font-size: 11px; color: ${textMuted}; margin-top: 2px; }
    .pb-decl-cat-badge { font-size: 11px; font-weight: 600; color: ${accent}; background: ${badge}; padding: 2px 8px; border-radius: 10px; }
    .pb-decl-cat-chevron { width: 16px; height: 16px; color: ${textMuted}; transition: transform 0.2s; margin-inline-start: 8px; flex-shrink: 0; }
    .pb-decl-cat-chevron.open { transform: rotate(180deg); }
    .pb-decl-table-wrap { overflow-x: auto; }
    .pb-decl-table-wrap:focus-visible { outline: 2px solid ${accent}; outline-offset: -2px; }
    .pb-decl-table { width: 100%; border-collapse: collapse; font-size: 12px; }
    .pb-decl-table th { text-align: start; padding: 10px 24px; color: ${textMuted}; font-weight: 500; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; background: ${bgHeader}; border-bottom: 1px solid ${border}; white-space: nowrap; }
    .pb-decl-table td { padding: 10px 24px; border-bottom: 1px solid ${border}; vertical-align: top; }
    .pb-decl-table tr:last-child td { border-bottom: none; }
    .pb-decl-name { font-weight: 500; font-family: ui-monospace, SFMono-Regular, 'SF Mono', Menlo, monospace; font-size: 11px; }
    .pb-decl-3p { font-size: 10px; color: ${textMuted}; background: ${badge}; padding: 1px 6px; border-radius: 6px; white-space: nowrap; }
    .pb-decl-footer { padding: 12px 24px; border-top: 1px solid ${border}; font-size: 10px; color: ${textMuted}; display: flex; justify-content: space-between; align-items: center; }
    .pb-decl-footer a { color: ${accent}; text-decoration: none; }
    .pb-decl-footer a:hover { text-decoration: underline; }
    .pb-decl-empty { padding: 40px 24px; text-align: center; color: ${textMuted}; font-size: 13px; }
    .pb-decl-hidden { display: none; }
  `;
}

function chevronSvg(open: boolean): string {
  return `<svg class="pb-decl-cat-chevron${open ? " open" : ""}" aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`;
}

function renderCategory(cat: string, cookies: CookieEntry[], columns: string[], index: number, lang: string): string {
  const info = categoryCopy(cat, lang);
  // The collapse-target id is derived from the render index, never from the
  // API-supplied category key, so a hostile key cannot reach an attribute/JS
  // context.
  const bodyId = `pb-decl-body-${index}`;

  const colMap: Record<string, { header: string; render: (c: CookieEntry) => string }> = {
    name: { header: t("declColCookie", lang), render: (c) => `<span class="pb-decl-name">${esc(c.name)}</span>` },
    provider: { header: t("declColProvider", lang), render: (c) => esc(c.provider || "—") },
    purpose: { header: t("declColPurpose", lang), render: (c) => esc(c.purpose || "—") },
    duration: { header: t("declColDuration", lang), render: (c) => esc(formatDuration(c.duration, c.duration_seconds, lang)) },
    type: { header: t("declColType", lang), render: (c) => c.is_third_party ? `<span class="pb-decl-3p">${esc(t("declThirdParty", lang))}</span>` : esc(t("declFirstParty", lang)) },
    lawful_basis: { header: t("declColLawfulBasis", lang), render: (c) => esc(c.lawful_basis || "—") },
  };

  const visibleCols = columns.filter((col) => colMap[col]);

  const headerRow = visibleCols.map((col) => `<th scope="col">${esc(colMap[col].header)}</th>`).join("");
  const bodyRows = cookies
    .map(
      (c) =>
        `<tr>${visibleCols.map((col) => `<td>${colMap[col].render(c)}</td>`).join("")}</tr>`
    )
    .join("");

  return `
    <div class="pb-decl-cat">
      <button type="button" class="pb-decl-cat-header" data-decl-toggle aria-expanded="true" aria-controls="${bodyId}">
        <div>
          <div class="pb-decl-cat-label">${esc(info.label)}</div>
          <div class="pb-decl-cat-desc">${esc(info.description)}</div>
        </div>
        <div style="display:flex;align-items:center">
          <span class="pb-decl-cat-badge">${cookies.length}</span>
          ${chevronSvg(true)}
        </div>
      </button>
      <div id="${bodyId}" class="pb-decl-body">
        <div class="pb-decl-table-wrap" tabindex="0" role="region" aria-label="${esc(info.label)}">
          <table class="pb-decl-table">
            <thead><tr>${headerRow}</tr></thead>
            <tbody>${bodyRows}</tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

function esc(s: string): string {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function renderWidget(data: DeclarationResponse, columns: string[], theme: "light" | "dark", lang: string): string {
  const styles = getStyles(theme);
  const catOrder = ["strictly_necessary", "functional", "analytics", "advertising", "social", "unknown"];
  const catHtml = catOrder
    .filter((cat) => data.categories[cat])
    .map((cat, index) => renderCategory(cat, data.categories[cat], columns, index, lang))
    .join("");

  const lastScanned = data.last_scanned
    ? new Date(data.last_scanned).toLocaleDateString(lang, { year: "numeric", month: "long", day: "numeric" })
    : t("declNever", lang);

  const countText = (data.total_cookies === 1 ? t("declCookieCountOne", lang) : t("declCookieCount", lang))
    .replace("{count}", String(data.total_cookies))
    .replace("{domain}", esc(data.domain));

  const dir = RTL_LANGUAGES.has(lang) ? "rtl" : "ltr";

  return `
    <style>${styles}</style>
    <div class="pb-decl" dir="${dir}" lang="${esc(lang)}">
      <div class="pb-decl-header">
        <h3 class="pb-decl-title">${esc(t("declTitle", lang))}</h3>
        <p class="pb-decl-subtitle">${countText} &middot; ${esc(t("declLastScanned", lang))} ${esc(lastScanned)}</p>
      </div>
      ${catHtml || `<div class="pb-decl-empty">${esc(t("declEmpty", lang))}</div>`}
      <div class="pb-decl-footer">
        <span>${esc(t("poweredBy", lang))} <a href="https://beacon.pritect.ai" target="_blank" rel="noopener noreferrer">Pritect Beacon</a></span>
        <span>${esc(t("declUpdated", lang))} ${esc(new Date(data.generated_at).toLocaleDateString(lang))}</span>
      </div>
    </div>
  `;
}

/**
 * Delegated click handler for the category collapse buttons. Attached once on
 * the widget container (no inline onclick, so host-page CSP without
 * 'unsafe-inline' keeps working); Enter/Space come free with real <button>s.
 * Exported for unit tests.
 */
export function onDeclToggleClick(e: Event): void {
  const target = e.target as HTMLElement | null;
  const btn = target?.closest?.("button[data-decl-toggle]") as HTMLButtonElement | null;
  if (!btn) return;
  const body = document.getElementById(btn.getAttribute("aria-controls") || "");
  if (!body) return;
  const nowHidden = body.classList.toggle("pb-decl-hidden");
  btn.setAttribute("aria-expanded", String(!nowHidden));
  btn.querySelector(".pb-decl-cat-chevron")?.classList.toggle("open", !nowHidden);
}

function detectTheme(attr: string | null): "light" | "dark" {
  if (attr === "light") return "light";
  if (attr === "dark") return "dark";
  // Auto-detect from page background
  if (typeof window !== "undefined") {
    const bg = getComputedStyle(document.body).backgroundColor;
    if (bg) {
      const match = bg.match(/\d+/g);
      if (match && match.length >= 3) {
        const lum = (parseInt(match[0]) * 299 + parseInt(match[1]) * 587 + parseInt(match[2]) * 114) / 1000;
        return lum < 128 ? "dark" : "light";
      }
    }
  }
  return "light";
}

export function initDeclarationWidget(): void {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;

  const domainId = script.getAttribute("data-domain-id");
  if (!domainId) {
    console.warn("[Pritect Declaration] Missing data-domain-id attribute");
    return;
  }

  const theme = detectTheme(script.getAttribute("data-theme"));
  const lang = script.getAttribute("data-lang") || navigator.language.split("-")[0] || "en";
  const columnsAttr = script.getAttribute("data-columns");
  const columns = columnsAttr
    ? columnsAttr.split(",").map((c) => c.trim())
    : ["name", "provider", "purpose", "duration", "type"];

  // Determine API base from script src
  const srcUrl = script.src;
  const supabaseBase = srcUrl.includes("supabase.co")
    ? srcUrl.split("/storage/")[0]
    : "https://ynbeopknlxoayzdocsmu.supabase.co";

  const apiUrl = `${supabaseBase}/functions/v1/cookie-declaration?domain_id=${encodeURIComponent(domainId)}`;

  // Find or create container
  const render = () => {
    let container = document.getElementById("pritect-cookie-declaration");
    if (!container) {
      container = document.createElement("div");
      container.id = "pritect-cookie-declaration";
      script.parentNode?.insertBefore(container, script.nextSibling);
    }

    // One delegated listener survives innerHTML re-renders; guard against
    // double-binding if render() ever runs twice.
    if (!container.dataset["pbDeclBound"]) {
      container.dataset["pbDeclBound"] = "1";
      container.addEventListener("click", onDeclToggleClick);
    }

    fetch(apiUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: DeclarationResponse) => {
        container!.innerHTML = renderWidget(data, columns, theme, lang);
      })
      .catch((err) => {
        console.warn("[Pritect Declaration] Failed to load cookie data:", err);
        const errColor = theme === "dark" ? "#a3a3b2" : "#595e6b";
        container!.innerHTML = `<div style="padding:20px;text-align:center;color:${errColor};font-size:13px">${esc(t("declError", lang))}</div>`;
      });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", render);
  } else {
    render();
  }
}
